// src/components/TemperaturePanel.jsx
// =====================  WHERE THIS GOES  =====================
// New file. Twin of WindPanel.jsx / SunPathPanel.jsx. Rendered by
// ClimateAnalysisPanel when the user opens "Temperature Analysis". Talks to
// the 3D scene only through temperatureStore. Works before any massing/fetch:
// the manual clear-sky path drives the heatmap immediately; NASA POWER (via
// /api/climate) fills the regional resource + outdoor temperature/humidity.
//
// Design-stage decision support, three cards over one calc core:
//   • Passive Ventilation → target openable window area (wind + stack)
//   • Active HVAC → peak TR + recommended type
//   • Materials → facade/interior shortlist vs ECBC-zone targets
// Plus rooftop PV, the facade+roof irradiation table, and a heat/cold strip.
// Everything is indicative screening — labelled as such.
//
// API_BASE mirrors Site3DView / WindPanel — centralise later.
// ============================================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MONTHS } from '../utils/solar';
import { useTemperature, setResults } from '../utils/temperatureStore';
import { API_BASE } from '../utils/apiBase';
import {
  MATERIALS, assemblyUValue, arealHeatCapacity, periodicResponse, massClass,
  ecbcZone, ZONE_INFO, comfortVerdict, openableAreaForACH, peakCoolingLoad,
  hvacTypeRecommendation, pvYield, humidityRatio, DIM, heatCss, FACE_NAME, PV_DEFAULTS, surfaceTemperatures } from '../utils/temperature';

const FACES = [{ key: 'N', name: 'North' }, { key: 'E', name: 'East' }, { key: 'S', name: 'South' }, { key: 'W', name: 'West' }];

// ---- wall / roof assembly presets (editable defaults from MATERIALS) ----
const WALL_PRESETS = {
  brick230:  { label: '230 brick + plaster', layers: [{ mat: 'cementPlaster', t: 0.012 }, { mat: 'brick', t: 0.23 }, { mat: 'cementPlaster', t: 0.012 }] },
  aac200:    { label: '200 AAC + plaster',   layers: [{ mat: 'cementPlaster', t: 0.012 }, { mat: 'aac', t: 0.20 }, { mat: 'gypsum', t: 0.012 }] },
  rcc150:    { label: '150 RCC + plaster',   layers: [{ mat: 'cementPlaster', t: 0.012 }, { mat: 'rcc', t: 0.15 }, { mat: 'cementPlaster', t: 0.012 }] },
  brickXPS:  { label: '230 brick + 50 XPS',  layers: [{ mat: 'cementPlaster', t: 0.012 }, { mat: 'brick', t: 0.23 }, { mat: 'xps', t: 0.05 }, { mat: 'gypsum', t: 0.012 }] },
};
const ROOF_PRESETS = {
  rcc150:   { label: '150 RCC + screed',   layers: [{ mat: 'rcc', t: 0.15 }, { mat: 'cementPlaster', t: 0.02 }] },
  rccXPS:   { label: '150 RCC + 50 XPS',   layers: [{ mat: 'rcc', t: 0.15 }, { mat: 'xps', t: 0.05 }, { mat: 'cementPlaster', t: 0.02 }] },
  aacRoof:  { label: '125 RCC + 75 AAC',   layers: [{ mat: 'rcc', t: 0.125 }, { mat: 'aac', t: 0.075 }, { mat: 'cementPlaster', t: 0.02 }] },
};
const USE_DEFAULTS = {
  residential: { internalWm2: 10, freshACH: 0.8, ventACH: 5, glassU: 3.0, wwr: 0.25 },
  office:      { internalWm2: 30, freshACH: 1.0, ventACH: 6, glassU: 3.0, wwr: 0.4 },
};

export default function TemperaturePanel({ project }) {
  const { params, results, status, climate, setParams, setClimate, runAnalysis, activate, deactivate } = useTemperature();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [useType, setUseType] = useState('residential');
  const [wallKey, setWallKey] = useState('brick230');
  const [roofKey, setRoofKey] = useState('rcc150');
  const [env, setEnv] = useState({ ...USE_DEFAULTS.residential, tIn: 25, absorptance: 0.6 });
  const [passive, setPassive] = useState({ targetACH: 5, dHeight: 1.5, manualWind: 2.0 });
  const [comfortLevel, setComfortLevel] = useState(80);
  const [pv, setPv] = useState({ ...PV_DEFAULTS });

  useEffect(() => { activate(); return () => deactivate(); }, []); // eslint-disable-line

  // seed lat/lng + load cached climate if present
  useEffect(() => {
    const lat = project?.centerLat, lng = project?.centerLng;
    if (lat != null && lng != null) setParams({ lat: +lat, lng: +lng });
    if (project?.cachedClimate?.perMonth) setClimate(project.cachedClimate);
    // eslint-disable-next-line
  }, [project?.id]);

  // apply use-type defaults
  useEffect(() => { setEnv((s) => ({ ...s, ...USE_DEFAULTS[useType] })); setPassive((s) => ({ ...s, ventACH: USE_DEFAULTS[useType].ventACH })); }, [useType]);

  const persistClimate = async (payload) => {
    if (project?.id == null) return;
    try {
      const body = { cachedClimate: { ...payload, fetchedAt: new Date().toISOString() } };
      const res = await fetch(`${API_BASE}/api/projects/${project.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (res.ok) Object.assign(project, body);
    } catch (e) { console.warn('climate cache persist error', e); }
  };
  const fetchClimate = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${API_BASE}/api/climate?lat=${params.lat}&lng=${params.lng}`);
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'climate fetch failed');
      setClimate(d); setParams({ manual: false }); persistClimate(d);
    } catch (e) { setErr(e.message || 'Could not reach NASA POWER'); }
    finally { setLoading(false); }
  };

  // ---- derived: assemblies ----
  const wall = useMemo(() => {
    const layers = WALL_PRESETS[wallKey].layers;
    const { U } = assemblyUValue(layers, 'wall');
    const kappa = arealHeatCapacity(layers);
    const per = periodicResponse(layers);
    return { U, kappa, ...per, cls: massClass(kappa), layers };
  }, [wallKey]);
  const roof = useMemo(() => {
    const layers = ROOF_PRESETS[roofKey].layers;
    const { U } = assemblyUValue(layers, 'roof');
    const kappa = arealHeatCapacity(layers);
    return { U, kappa, cls: massClass(kappa), layers };
  }, [roofKey]);

  // ---- derived: climate month + zone ----
  const cm = climate?.perMonth?.[params.month] || null;
  const zone = useMemo(() => (climate?.perMonth ? ecbcZone(climate.perMonth) : null), [climate]);
  const zi = zone ? ZONE_INFO[zone.zone] : null;

  // ---- derived: geometry from last analysis ----
  const geom = results?.geom || null;

  // ---- Passive ventilation ----
  const passiveOut = useMemo(() => {
    if (!geom || !cm) return null;
    const windMonth = project?.cachedWind?.perMonth?.find((m) => m.month === params.month);
    const windSpeed = windMonth?.meanSpeed ?? passive.manualWind;
    const volume = geom.footprintArea * 3;                 // one ventilated floor (3 m)
    const dTemp = Math.max(2, Math.abs(cm.tMean - env.tIn));
    const out = openableAreaForACH({
      volume, ach: passive.targetACH, floorAreaM2: geom.footprintArea,
      windSpeed, dHeight: passive.dHeight, dTemp,
    });
    return { ...out, windSpeed, windFromWind: !!windMonth, dTemp };
  }, [geom, cm, passive, env.tIn, params.month, project]);

  // ---- Active HVAC (design-day peak) ----
  // Surface temperature stands on its own: it needs only the sweep's per-facade
  // irradiance, so it must not sit behind the HVAC memo's climate guard. Uses
  // the SELECTED month's air temperature (the HVAC sizing deliberately uses the
  // hottest month, which is the wrong basis for "how hot is this facade now").
  // Without climate data it still reports the rise and leaves absolute null.
  const surfOut = useMemo(() => {
    if (!results?.wm2Card) return null;
    const m = climate?.perMonth?.[params.month];
    return surfaceTemperatures(
      results.wm2Card, results.roofMeanDaytimeWm2,
      params.absorptance, m?.tMax ?? null
    );
  }, [results, climate, params.month, params.absorptance]);

  const hvacOut = useMemo(() => {
    if (!geom || !climate?.perMonth) return null;
    const tMax = Math.max(...climate.perMonth.map((m) => m.tMax ?? 0));
    const hotMonth = climate.perMonth.reduce((a, b) => ((b.tMax ?? 0) > (a.tMax ?? 0) ? b : a));
    const wallOpaque = geom.wallArea * (1 - env.wwr);
    const glazing = geom.wallArea * env.wwr;
    const envUA = wall.U * wallOpaque + roof.U * geom.roofArea + env.glassU * glazing;
    const peakGlazingWm2 = Math.max(...Object.values(results.wm2Card || { x: 400 }), 300);
    const opaqueSolarW = env.absorptance * peakGlazingWm2 * wallOpaque * (wall.U / 22);
    const ventM3s = env.freshACH * (geom.volume) / 3600;
    const load = peakCoolingLoad({
      envUA, glazingArea: glazing, shgc: params.shgc, peakGlazingWm2,
      opaqueSolarW, tOutDesign: tMax + (params.showUHI ? results.uhi.deltaT : 0), tIn: env.tIn,
      internalW: env.internalWm2 * geom.floorArea, ventM3s, rhOut: hotMonth.rh ?? 55, rhIn: 55,
    });
    const rec = hvacTypeRecommendation({ tr: load.tr, latentFraction: load.latentFraction, zone: zone?.zone, floorAreaM2: geom.floorArea });
        return { ...load, rec, tMax };
  }, [geom, climate, wall, roof, env, params.shgc, params.showUHI, results, zone]);

  // ---- 12-month heat/cold strip ----
  const strip = useMemo(() => {
    if (!climate?.perMonth) return null;
    return climate.perMonth.map((m) => {
      const v = comfortVerdict(m.tMean, m.tMean, comfortLevel);
      return { month: m.month, need: v.need, tMean: m.tMean };
    });
  }, [climate, comfortLevel]);

  // ---- rooftop PV ----
  const pvOut = useMemo(() => {
    if (!results?.roofYear || !geom) return null;
    const roofYear = results.roofYear();
    return { ...pvYield(roofYear, geom.roofArea, pv), roofYear };
  }, [results, geom, pv]);

  // ---- styles (identical tokens to WindPanel) ----
  const card = { background: 'var(--surface-2,#1a2234)', border: '0.5px solid var(--border,#3a4456)', borderRadius: 12, padding: 16, marginBottom: 12 };
  const lblRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, marginBottom: 6, color: 'var(--text-secondary,#c3c9d4)' };
  const val = { fontFamily: 'ui-monospace,Menlo,monospace', color: 'var(--text-primary,#e5e7eb)', fontSize: 13 };
  const big = { ...val, fontSize: 18 };
  // boxSizing is the fix: width:100% plus padding and a border, with the default
  // content-box, made each of these WIDER than the third of a row it sits in —
  // so three side by side overflowed and clipped their own values. minWidth:0
  // lets a flex child actually shrink; without it the text still wins.
  //
  // --field rather than --surface-1: the card behind these is --surface-1 too,
  // so the box was the colour of its own background and only a half-pixel
  // border said it was a field at all.
  const numIn = {
    width: '100%', minWidth: 0, boxSizing: 'border-box',
    padding: '6px 8px', fontSize: 12.5,
    fontFamily: 'ui-monospace,Menlo,monospace',
    border: '1px solid var(--field-border, #818d9c)', borderRadius: 8,
    background: 'var(--field, #fff)', color: 'var(--field-ink, #111827)',
  };
  const sel = { ...numIn, cursor: 'pointer' };
  const muted = { fontSize: 11, color: 'var(--text-muted,#8b92a0)' };
  // INVALIDATE on any result-changing param (see SunPathPanel): showing a
  // previous run's numbers under new inputs is stale data with a fresh label.
  const firstParams = useRef(true);
  useEffect(() => {
    if (firstParams.current) { firstParams.current = false; return; }
    setResults(null);
    // eslint-disable-next-line
  }, [params.month, params.clearness, params.albedo, params.shgc, params.absorptance, params.tIn]);

  // Blue at rest, green while active — matches the Design Analysis button.
  const btn = (a, disabled) => ({
    flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', borderRadius: 8,
    border: `0.5px solid ${a ? 'var(--ok,#3ecf8e)' : 'var(--border-accent,#378add)'}`,
    background: a ? 'var(--ok,#3ecf8e)' : 'var(--border-accent,#378add)',
    color: a ? '#01212a' : '#fff', opacity: disabled ? 0.45 : 1,
  });
  const Toggle = ({ k, label }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', color: params[k] ? 'var(--text-primary,#e5e7eb)' : 'var(--text-secondary,#8b92a0)' }}>
      <input type="checkbox" checked={!!params[k]} onChange={(e) => setParams({ [k]: e.target.checked })} style={{ accentColor: 'var(--border-accent,#378add)' }} />
      {label}
    </label>
  );
  const field = (label, node) => (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 10, color: 'var(--text-muted,#8b92a0)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>{label}</div>
      {node}
    </div>
  );

  const max = results ? Math.max(0.001, results.roofKWhm2Month, ...Object.values(results.perCard)) : 1;
  const ordered = results ? [...FACES].sort((a, b) => results.perCard[b.key] - results.perCard[a.key]) : FACES;
  const NEED_COLOR = { cooling: '#ef6c3a', heating: '#3b82f6', comfort: '#2a9d8f' };

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* preconditions */}
      {!status.hasMassing && (
        <div style={{ ...card, border: '1px solid var(--border-accent)', fontSize: 12, color: 'var(--text-secondary,#c3c9d4)' }}>
          Build the massing first (left panel → Build) to compute per-facade irradiation, window sizing and loads. The climate fetch & material calcs below work now.
        </div>
      )}
      {status.hasMassing && !status.hasContext && (
        <div style={{ ...card, border: '1px solid #8a6d1f', background: 'rgba(138,109,31,0.12)', color: '#e6c34d', fontSize: 12 }}>
          Enable <b>Site Context</b> in the 3D view to include self- and neighbour-shading — otherwise every facade reads fully sun-exposed.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', ...muted, margin: '0 2px 12px' }}>
        <span>{MONTHS[params.month]} · {cm ? `${cm.tMean?.toFixed(0)}°C, ${cm.rh?.toFixed(0)}% RH` : 'clear-sky (no climate data)'}</span>
        <span>{(+params.lat).toFixed(2)}°, {(+params.lng).toFixed(2)}°</span>
      </div>

      {/* climate source */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Climate source</span>
          <span style={{ fontSize: 11, color: params.manual ? '#e6c34d' : 'var(--text-accent,#7fb4f0)' }}>{params.manual ? 'Manual clear-sky' : (climate?.source || 'NASA POWER')}</span>
        </div>
        <button style={btn(false, loading)} onClick={fetchClimate} disabled={loading}>
          {loading ? 'Fetching…' : (climate ? '↻ Refresh climate (NASA POWER)' : 'Fetch climate (NASA POWER)')}
        </button>
        {err && <div style={{ ...muted, color: '#ef6c3a', marginTop: 8 }}>{err}</div>}
        {params.manual && (
          <div style={{ marginTop: 10 }}>
            <div style={lblRow}><span>Clear-sky clearness</span><span style={val}>{(+params.clearness).toFixed(2)}</span></div>
            <input type="range" min={0.4} max={1} step={0.05} value={params.clearness} onChange={(e) => setParams({ clearness: +e.target.value })} style={{ width: '100%', accentColor: '#378add' }} />
            <div style={{ ...muted, marginTop: 6 }}>Clear-sky ignores clouds and overstates gain. Fetch NASA POWER for real monthly GHI/DNI/DHI + outdoor temperature & humidity (needed for loads, zone & comfort).</div>
          </div>
        )}

        <div style={{ ...lblRow, marginTop: 14 }}><span>Month</span><span style={val}>{MONTHS[params.month]}</span></div>
        <input type="range" min={0} max={11} step={1} value={params.month} onChange={(e) => setParams({ month: +e.target.value })} style={{ width: '100%', accentColor: '#378add' }} />

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button style={btn(false, !status.hasMassing)} onClick={() => status.hasMassing && runAnalysis()} disabled={!status.hasMassing}>Analyze</button>
        </div>
      </div>

      {/* visibility toggles */}
      <div style={card}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Toggle k="showFacade" label="Facade heatmap" />
          <Toggle k="showRoof" label="Roof heatmap" />
          <Toggle k="showUHI" label="Urban heat-island" />
        </div>
        {results?.uhi && params.showUHI && (
          <div style={{ ...muted, marginTop: 8 }}>
            Indicative UHI ≈ <b style={{ color: '#e6c34d' }}>+{results.uhi.deltaT.toFixed(1)} °C</b> from context ({(results.uhi.builtFrac * 100).toFixed(0)}% built, {(results.uhi.greenFrac * 100).toFixed(0)}% green). Not measured — a screening offset added to outdoor temperature.
          </div>
        )}
      </div>

      {/* heat / cold strip */}
      {strip && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>Heat / cold by month</div>
          <div style={{ ...muted, marginBottom: 10 }}>IMAC {comfortLevel}% adaptive band · free-running indoor ≈ outdoor</div>
          <div style={{ display: 'flex', gap: 3 }}>
            {strip.map((s) => (
              <div key={s.month} title={`${MONTHS[s.month]} · ${s.tMean?.toFixed(0)}°C · ${s.need}`} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ height: 26, borderRadius: 4, background: NEED_COLOR[s.need], opacity: s.month === params.month ? 1 : 0.55, cursor: 'pointer', border: s.month === params.month ? '1px solid #e5e9f0' : 'none' }} onClick={() => setParams({ month: s.month })} />
                <div style={{ fontSize: 8, color: 'var(--text-muted,#8b92a0)', marginTop: 3 }}>{MONTHS[s.month][0]}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 10, ...muted }}>
            <span><span style={{ color: NEED_COLOR.cooling }}>■</span> cooling</span>
            <span><span style={{ color: NEED_COLOR.comfort }}>■</span> passive OK</span>
            <span><span style={{ color: NEED_COLOR.heating }}>■</span> heating</span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {[80, 90].map((l) => <button key={l} style={{ ...btn(comfortLevel === l), flex: 'none', padding: '2px 8px' }} onClick={() => setComfortLevel(l)}>{l}%</button>)}
            </span>
          </div>
        </div>
      )}

      {/* irradiation table */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Incident irradiation by surface</div>
        <div style={{ ...muted, marginBottom: 10 }}>kWh/m² for {MONTHS[params.month]} · obstruction-aware · {results ? (results.sourceMode === 'data' ? 'NASA POWER' : 'clear-sky') : '—'}</div>
        {!results && <div style={{ fontSize: 12, color: 'var(--text-secondary,#c3c9d4)' }}>Press <b>Analyze</b> to integrate irradiation on each facade + roof.</div>}
        {results && [...ordered, { key: 'ROOF', name: 'Roof' }].map((F) => {
          const v = F.key === 'ROOF' ? results.roofKWhm2Month : results.perCard[F.key];
          const frac = v / max;
          return (
            <div key={F.key} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0', fontSize: 12 }}>
              <span style={{ width: 44, color: 'var(--text-secondary,#c3c9d4)' }}>{F.name}</span>
              <span style={{ flex: 1, height: 9, background: 'var(--surface-1,#141a28)', borderRadius: 5, overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', width: `${(frac * 100).toFixed(0)}%`, background: heatCss(frac), borderRadius: 5 }} />
              </span>
              <span style={{ width: 70, textAlign: 'right', ...muted }}>{v.toFixed(1)} kWh/m²</span>
            </div>
          );
        })}
        {results && (
          <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary,#c3c9d4)', background: 'var(--surface-1,#141a28)', border: '0.5px solid var(--border,#3a4456)', borderRadius: 8, padding: '9px 10px' }}>
            Hottest facade: <b style={{ color: '#ef6c3a' }}>{FACE_NAME[results.hi]}</b> · coolest: <b style={{ color: 'var(--accent)' }}>{FACE_NAME[results.lo]}</b>. Prioritise shading & lower SHGC on {FACE_NAME[results.hi]}.
          </div>
        )}

        {surfOut && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border,#3a4456)' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted,#8b92a0)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>
              Surface temperature{surfOut.ambientC != null ? ` · air ${surfOut.ambientC.toFixed(1)}°C` : ''}
            </div>
            {['N', 'E', 'S', 'W', 'R'].map((c) => {
              const d = surfOut[c];
              return (
                <div key={c} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 11.5, marginBottom: 3 }}>
                  <span style={{ width: 46, color: 'var(--text-secondary,#c3c9d4)' }}>{FACE_NAME[c] || 'Roof'}</span>
                  <span style={{ flex: 1, color: 'var(--text-muted,#8b92a0)', fontSize: 10.5 }}>{d.incidentWm2} W/m²</span>
                  <b style={{ color: d.riseK >= 15 ? '#ef6c3a' : d.riseK >= 8 ? '#e6c34d' : 'var(--text-primary,#e5e7eb)' }}>
                    +{d.riseK.toFixed(1)} K
                  </b>
                  {d.surfaceC != null && (
                    <span style={{ width: 56, textAlign: 'right', color: 'var(--text-primary,#e5e7eb)' }}>
                      {d.surfaceC.toFixed(1)}°C
                    </span>
                  )}
                </div>
              );
            })}
            <div style={{ fontSize: 10, color: 'var(--text-muted,#8b92a0)', marginTop: 7, lineHeight: 1.45 }}>
              Sol-air, daytime average at α&nbsp;={surfOut.absorptance} — not the instantaneous
              peak, which runs higher near solar noon. Assumes h₀&nbsp;=&nbsp;22&nbsp;W/m²K
              (moderate wind); a sheltered facade runs hotter, an exposed one cooler.
              {surfOut.ambientC == null && ' No climate data — showing the rise only.'}
            </div>
          </div>
        )}
      </div>

      {/* ===== CARD 1 — PASSIVE VENTILATION ===== */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Passive ventilation — window sizing</div>
        <div style={{ ...muted, marginBottom: 10 }}>openable area per floor · wind + stack · floor-level target, not per-room</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          {field('Use', <select value={useType} style={sel} onChange={(e) => setUseType(e.target.value)}><option value="residential">Residential</option><option value="office">Office</option></select>)}
          {field('Target ACH', <input type="number" step={0.5} value={passive.targetACH} style={numIn} onChange={(e) => setPassive((s) => ({ ...s, targetACH: +e.target.value }))} />)}
          {field('Inlet↕outlet m', <input type="number" step={0.5} value={passive.dHeight} style={numIn} onChange={(e) => setPassive((s) => ({ ...s, dHeight: +e.target.value }))} />)}
        </div>
        {!passiveOut && <div style={muted}>Build the massing and fetch climate to size openings.</div>}
        {passiveOut && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div><div style={muted}>Openable area / floor</div><div style={big}>{passiveOut.governing.toFixed(1)} m²</div></div>
              <div><div style={muted}>% of floor area</div><div style={big}>{passiveOut.pctOfFloor.toFixed(1)}%</div></div>
            </div>
            <div style={{ display: 'flex', height: 9, borderRadius: 5, overflow: 'hidden', margin: '12px 0 6px' }}>
              <span style={{ width: `${(passiveOut.windShare * 100).toFixed(0)}%`, background: '#38bdf8' }} />
              <span style={{ width: `${(passiveOut.stackShare * 100).toFixed(0)}%`, background: '#e6c34d' }} />
            </div>
            <div style={muted}>
              <span style={{ color: '#38bdf8' }}>■</span> wind {(passiveOut.windShare * 100).toFixed(0)}% ({passiveOut.windSpeed.toFixed(1)} m/s{passiveOut.windFromWind ? ', from Wind tool' : ', manual'}) · <span style={{ color: '#e6c34d' }}>■</span> stack {(passiveOut.stackShare * 100).toFixed(0)}% (ΔT {passiveOut.dTemp.toFixed(0)}K)
            </div>
            <div style={{ ...muted, marginTop: 8 }}>
              Airflow needs {passiveOut.physicsArea === Infinity ? '—' : `${passiveOut.physicsArea.toFixed(1)} m²`}; NBC floor {passiveOut.codeFloor.toFixed(1)} m² (10% of floor). Provide the larger. Put inlets on the windward facade ({project?.cachedWind ? 'per Wind tool' : 'run Wind tool'}), outlets leeward/high.
            </div>
          </>
        )}
      </div>

      {/* ===== CARD 2 — ACTIVE HVAC ===== */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Active HVAC — size & type</div>
        <div style={{ ...muted, marginBottom: 10 }}>design-day peak (hottest month T-max) · screening, not a load calc</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          {field('Indoor °C', <input type="number" step={0.5} value={env.tIn} style={numIn} onChange={(e) => setEnv((s) => ({ ...s, tIn: +e.target.value }))} />)}
          {field('WWR', <input type="number" step={0.05} value={env.wwr} style={numIn} onChange={(e) => setEnv((s) => ({ ...s, wwr: +e.target.value }))} />)}
          {field('Glass U', <input type="number" step={0.1} value={env.glassU} style={numIn} onChange={(e) => setEnv((s) => ({ ...s, glassU: +e.target.value }))} />)}
        </div>
        <div style={{ ...lblRow }}><span>SHGC (glazing)</span><span style={val}>{(+params.shgc).toFixed(2)}</span></div>
        <input type="range" min={0.1} max={0.9} step={0.05} value={params.shgc} onChange={(e) => setParams({ shgc: +e.target.value })} style={{ width: '100%', accentColor: '#378add' }} />
        {!hvacOut && <div style={{ ...muted, marginTop: 10 }}>Build the massing and fetch climate to size HVAC.</div>}
        {hvacOut && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
              <div><div style={muted}>Peak cooling</div><div style={big}>{hvacOut.tr.toFixed(1)} TR</div><div style={muted}>{hvacOut.totalKW.toFixed(1)} kW</div></div>
              <div><div style={muted}>Latent share</div><div style={big}>{(hvacOut.latentFraction * 100).toFixed(0)}%</div><div style={muted}>{hvacOut.latentKW.toFixed(1)} kW latent</div></div>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary,#c3c9d4)', background: 'var(--surface-1,#141a28)', border: '0.5px solid var(--border,#3a4456)', borderRadius: 8, padding: '9px 10px' }}>
              <b style={{ color: '#7fb4f0' }}>{hvacOut.rec.primary.type}</b> — {hvacOut.rec.primary.why}
              {hvacOut.rec.options.length > 1 && <div style={{ ...muted, marginTop: 6 }}>Alternatives: {hvacOut.rec.options.slice(1).map((o) => o.type).join(' · ')}</div>}
            </div>
            <div style={{ ...muted, marginTop: 8 }}>
              Envelope {hvacOut.parts.envelopeKW.toFixed(1)} · glass {hvacOut.parts.glassKW.toFixed(1)} · vent-sensible {hvacOut.parts.ventSensKW.toFixed(1)} · vent-latent {hvacOut.parts.ventLatKW.toFixed(1)} · internal {hvacOut.parts.internalKW.toFixed(1)} kW. Monthly means undersize — this uses design-day T-max ({hvacOut.tMax.toFixed(0)}°C).
            </div>
          </>
        )}
      </div>

      {/* ===== CARD 3 — MATERIALS ===== */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Materials — facade & roof</div>
        <div style={{ ...muted, marginBottom: 10 }}>{zi ? <>Zone: <b style={{ color: '#7fb4f0' }}>{zi.label}</b> · {zi.note}</> : 'Fetch climate to resolve the ECBC zone & targets.'}</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          {field('Wall build-up', <select value={wallKey} style={sel} onChange={(e) => setWallKey(e.target.value)}>{Object.entries(WALL_PRESETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>)}
          {field('Roof build-up', <select value={roofKey} style={sel} onChange={(e) => setRoofKey(e.target.value)}>{Object.entries(ROOF_PRESETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 4 }}>
          <div><div style={muted}>Wall U</div><div style={val}>{wall.U.toFixed(2)} W/m²K</div></div>
          <div><div style={muted}>Thermal mass</div><div style={val}>{wall.kappa.toFixed(0)} kJ/m²K</div></div>
          <div><div style={muted}>Response</div><div style={val}>{wall.cls}</div></div>
          <div><div style={muted}>Roof U</div><div style={val}>{roof.U.toFixed(2)} W/m²K</div></div>
          <div><div style={muted}>Time lag ≈</div><div style={val}>{wall.timeLagH.toFixed(1)} h</div></div>
          <div><div style={muted}>Decrement ≈</div><div style={val}>{wall.decrement.toFixed(2)}</div></div>
        </div>
        {zi && (
          <div style={{ marginTop: 12, fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary,#c3c9d4)', background: 'var(--surface-1,#141a28)', border: '0.5px solid var(--border,#3a4456)', borderRadius: 8, padding: '9px 10px' }}>
            {(() => {
              const uOk = wall.U <= zi.targetU * 1.15;
              const massOk = zi.wantMass === wall.cls || (zi.wantMass === 'heavy' && wall.cls === 'heavy') || (zi.wantMass === 'medium' && wall.cls !== 'light');
              return <>Target: U ≤ {zi.targetU} W/m²K, {zi.wantMass} mass, SHGC ≈ {zi.targetSHGC}. Wall U {uOk ? '✓ meets' : '✗ too high — add insulation'}; mass {massOk ? '✓ suits zone' : `✗ wants ${zi.wantMass}`}. {zi.wantMass === 'heavy' ? 'Heavy walls + long time-lag shift peak heat to night, when you can flush it.' : 'Lightweight, shaded, ventilated envelope sheds heat fast.'}</>;
            })()}
          </div>
        )}
        <div style={{ ...muted, marginTop: 8 }}>Time-lag / decrement are approximate (admittance method is the exact upgrade). U-value & thermal mass are robust.</div>
      </div>

      {/* ===== rooftop PV ===== */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Rooftop PV — screening</div>
        <div style={{ ...muted, marginBottom: 10 }}>roof GHI × area × η × PR · feasibility triage, not a bankable yield</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {field('η module', <input type="number" step={0.01} value={pv.moduleEff} style={numIn} onChange={(e) => setPv((s) => ({ ...s, moduleEff: +e.target.value }))} />)}
          {field('PR', <input type="number" step={0.05} value={pv.performanceRatio} style={numIn} onChange={(e) => setPv((s) => ({ ...s, performanceRatio: +e.target.value }))} />)}
          {field('Roof use', <input type="number" step={0.05} value={pv.coverage} style={numIn} onChange={(e) => setPv((s) => ({ ...s, coverage: +e.target.value }))} />)}
        </div>
        {!pvOut && <div style={{ ...muted, marginTop: 10 }}>Analyze with a massing to estimate roof yield.</div>}
        {pvOut && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
            <div><div style={muted}>Annual est.</div><div style={big}>{Math.round(pvOut.annualKWh).toLocaleString()} kWh</div></div>
            <div><div style={muted}>Array (cap)</div><div style={big}>{pvOut.capKWp.toFixed(1)} kWp</div><div style={muted}>{pvOut.arrayArea.toFixed(0)} m²</div></div>
          </div>
        )}
      </div>
    </div>
  );
}
